// src/features/authz/CheckSimulator.tsx
// Single authorization check: subject / relation / object → allow / deny.
// See docs/ux/authz-ia.md section 1.

import { useState } from 'react';
import { useI18n, useAuthz } from '@react/hooks';
import { Button, Input, ErrorAlert } from '@react/components';
import type { CheckResponse } from '@core/types/authz';

export function CheckSimulator() {
  const { t } = useI18n();
  const store = useAuthz();
  const [user, setUser] = useState('user:');
  const [relation, setRelation] = useState('');
  const [object, setObject] = useState('');
  const [result, setResult] = useState<CheckResponse | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user.trim() || !relation.trim() || !object.trim()) return;
    setIsChecking(true);
    setResult(null);
    try {
      const r = await store.Check({ user: user.trim(), relation: relation.trim(), object: object.trim(), authorization_model_id: '' });
      setResult(r);
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <Input label={t('authz.check.user')} value={user} onChange={(e) => setUser(e.target.value)} placeholder="user:bob" className="w-56" required />
        <Input label={t('authz.check.relation')} value={relation} onChange={(e) => setRelation(e.target.value)} placeholder="viewer" className="w-40" required />
        <Input label={t('authz.check.object')} value={object} onChange={(e) => setObject(e.target.value)} placeholder="document:readme" className="w-56" required />
        <Button type="submit" size="sm" disabled={isChecking}>{isChecking ? t('common.loading') : t('authz.check.run')}</Button>
      </form>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      {result && (
        <div className={`rounded-md border p-4 ${result.allowed ? 'border-success/40 bg-success/5' : 'border-destructive/40 bg-destructive/5'}`}>
          <p className={`text-lg font-semibold ${result.allowed ? 'text-success' : 'text-destructive'}`}>
            {result.allowed ? t('authz.check.allowed') : t('authz.check.denied')}
          </p>
          {(result.missing_context?.missing_keys?.items?.length ?? 0) > 0 && (
            <p className="mt-1 text-sm text-muted-foreground">
              {t('authz.check.missingContext', { keys: result.missing_context!.missing_keys!.items.join(', ') })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
